import React, { useEffect } from "react";
import { FiX, FiClock, FiAward } from "react-icons/fi";
import { formatINR, initials, resolveImage } from "./consultationUtils";

export function ConsultantPhoto({ consultant, size = "md" }) {
  const src = resolveImage(consultant.photoUrl);
  return src ? (
    <img className={`cn-photo cn-photo-${size}`} src={src} alt={consultant.name} loading="lazy" />
  ) : (
    <span className={`cn-photo cn-photo-${size} cn-photo-fallback`} aria-hidden="true">
      {initials(consultant.name)}
    </span>
  );
}

export function ConsultantCard({ consultant, selected, onView, onSelect, selectLabel = "Select consultant" }) {
  return (
    <article className={`cn-consultant ${selected ? "is-selected" : ""}`}>
      <div className="cn-consultant-head">
        <ConsultantPhoto consultant={consultant} />
        <div>
          <h3>{consultant.name}</h3>
          {consultant.headline && <p className="cn-muted">{consultant.headline}</p>}
        </div>
      </div>

      <ul className="cn-meta">
        <li>
          <FiAward /> {consultant.experienceYears}+ years experience
        </li>
        <li>
          <FiClock /> {consultant.durationMinutes} min consultation
        </li>
      </ul>

      {consultant.specialisations?.length > 0 && (
        <div className="cn-chips">
          {consultant.specialisations.slice(0, 4).map((s) => (
            <span className="cn-chip" key={s}>
              {s}
            </span>
          ))}
        </div>
      )}

      <div className="cn-consultant-foot">
        <span className="cn-fee">{formatINR(consultant.fee)}</span>
        <div className="cn-btn-row">
          {onView && (
            <button type="button" className="cn-btn cn-btn-ghost" onClick={() => onView(consultant)}>
              View profile
            </button>
          )}
          <button type="button" className="cn-btn cn-btn-gold" onClick={() => onSelect(consultant)}>
            {selected ? "Selected ✓" : selectLabel}
          </button>
        </div>
      </div>
    </article>
  );
}

export function ConsultantModal({ consultant, onClose, onSelect }) {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  if (!consultant) return null;

  return (
    <div className="cn-modal-overlay" onClick={onClose}>
      <div className="cn-modal" role="dialog" aria-modal="true" aria-label={consultant.name} onClick={(e) => e.stopPropagation()}>
        <button type="button" className="cn-modal-close" onClick={onClose} aria-label="Close">
          <FiX />
        </button>

        <div className="cn-consultant-head">
          <ConsultantPhoto consultant={consultant} size="lg" />
          <div>
            <h3>{consultant.name}</h3>
            {consultant.headline && <p className="cn-muted">{consultant.headline}</p>}
          </div>
        </div>

        <ul className="cn-meta">
          <li>
            <FiAward /> {consultant.experienceYears}+ years experience
          </li>
          <li>
            <FiClock /> {consultant.durationMinutes} min consultation
          </li>
        </ul>

        {consultant.bio && <p className="cn-bio">{consultant.bio}</p>}

        {consultant.specialisations?.length > 0 && (
          <>
            <h4>Specialisation</h4>
            <div className="cn-chips">
              {consultant.specialisations.map((s) => (
                <span className="cn-chip" key={s}>
                  {s}
                </span>
              ))}
            </div>
          </>
        )}

        {consultant.languages?.length > 0 && (
          <p className="cn-muted cn-langs">Languages: {consultant.languages.join(", ")}</p>
        )}

        <div className="cn-consultant-foot">
          <span className="cn-fee">{formatINR(consultant.fee)}</span>
          <button type="button" className="cn-btn cn-btn-gold" onClick={() => onSelect(consultant)}>
            Select consultant
          </button>
        </div>
      </div>
    </div>
  );
}
